import DOMPurify from "dompurify";

/**
 * Helpers for rendering untrusted (server / DB / Salesforce / import) HTML.
 *
 * Use `sanitizeHtml()` for anything bound with `v-html`. The default DOMPurify
 * profile keeps normal formatting markup (b, i, p, ul/li, tables, links, inline
 * styles, classes, ...) and removes scripts, event handlers and `javascript:` URLs.
 */

const PURIFY_CONFIG = {
  // Keep `target` on links (e.g. target="_blank" in rich text).
  ADD_ATTR: ["target"],
  // Parse everything in body context, like `el.innerHTML = html` does, so a
  // leading <style> block is not moved into <head> and dropped.
  FORCE_BODY: true,
};

/** Sanitize an HTML string for use with `v-html`. null/undefined -> "". */
export function sanitizeHtml(html: unknown): string {
  if (html === null || html === undefined) return "";
  return DOMPurify.sanitize(String(html), PURIFY_CONFIG) as string;
}

/** Escape text so it can be safely concatenated into an HTML string. */
export function escapeHtml(text: unknown): string {
  if (text === null || text === undefined) return "";
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Escape a string so it matches literally inside a RegExp. */
export function escapeRegExp(text: unknown): string {
  if (text === null || text === undefined) return "";
  return String(text).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Extract the plain text of an HTML string without executing anything
 * (DOMParser documents are inert: no scripts run, no handlers fire, no images load).
 */
export function htmlToText(html: unknown): string {
  if (html === null || html === undefined) return "";
  // Leading "<body>" keeps everything (e.g. a leading <style>) in body context, like a detached div.
  const doc = new DOMParser().parseFromString(`<body>${String(html)}</body>`, "text/html");
  return doc.body.textContent || "";
}

/**
 * Sanitize `html` and wrap every case-insensitive literal match of `query` found in
 * its text nodes with `<span class="{className}">`. Returns an HTML string that is
 * safe for `v-html`. Markup inside the input is preserved (after sanitizing); the
 * query is matched literally, so characters like `(` or `[` are fine.
 */
export function highlightHtml(html: unknown, query: unknown, className = "text-primary-700"): string {
  const safe = sanitizeHtml(html);
  const q = query === null || query === undefined ? "" : String(query);
  if (!q) return safe;

  const regex = new RegExp(`(${escapeRegExp(q)})`, "gi");
  const doc = new DOMParser().parseFromString(`<body>${safe}</body>`, "text/html");

  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || "";
      const parts = text.split(regex);
      if (parts.length <= 1) return;
      const frag = doc.createDocumentFragment();
      parts.forEach((part, i) => {
        if (!part) return;
        // split() with a capturing group puts the matches at odd indexes
        if (i % 2 === 1) {
          const span = doc.createElement("span");
          span.className = className;
          span.textContent = part;
          frag.appendChild(span);
        } else {
          frag.appendChild(doc.createTextNode(part));
        }
      });
      node.parentNode?.replaceChild(frag, node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      Array.from(node.childNodes).forEach(walk);
    }
  };

  walk(doc.body);
  return doc.body.innerHTML;
}

/**
 * HTML-escape plain `text` and wrap every case-insensitive literal match of `query`
 * in `<{tag} class="{className}">`. Returns an HTML string that is safe for `v-html`.
 */
export function highlightText(text: unknown, query: unknown, tag = "span", className = "text-primary-700"): string {
  const str = text === null || text === undefined ? "" : String(text);
  const q = query === null || query === undefined ? "" : String(query);
  if (!q) return escapeHtml(str);
  const regex = new RegExp(`(${escapeRegExp(q)})`, "gi");
  return str
    .split(regex)
    .map((part, i) => (i % 2 === 1 ? `<${tag} class="${escapeHtml(className)}">${escapeHtml(part)}</${tag}>` : escapeHtml(part)))
    .join("");
}
